import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Check, Plus, X } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import {
  STATUS_CLASSES,
  dayLabel,
  fmtDate,
  fmtDays,
  fmtRange,
  todayIso,
  type PtoPreview,
  type PtoRequest,
  type PtoRosterRow,
} from '../pto/types.ts';

export function AdminPtoPage(): React.ReactElement {
  useDocumentTitle('Admin — PTO');
  const { agent } = useAuth();
  const toast = useToast();
  const [pending, setPending] = useState<PtoRequest[] | null>(null);
  const [roster, setRoster] = useState<PtoRosterRow[] | null>(null);
  const [history, setHistory] = useState<PtoRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, r, h] = await Promise.all([
        apiFetch<{ requests: PtoRequest[] }>('/api/pto/requests?status=pending'),
        apiFetch<{ agents: PtoRosterRow[] }>('/api/pto/roster'),
        apiFetch<{ requests: PtoRequest[] }>('/api/pto/requests?status=approved'),
      ]);
      setPending(p.requests);
      setRoster(r.agents);
      setHistory(h.requests);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load PTO.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, action: 'approve' | 'deny'): Promise<void> {
    const note = action === 'deny' ? window.prompt('Reason for denying (optional):') ?? '' : '';
    try {
      await apiFetch(`/api/pto/requests/${id}/${action}`, {
        method: 'POST',
        json: { note: note.trim() || undefined },
      });
      toast.success(action === 'approve' ? 'Approved.' : 'Denied.');
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : `Could not ${action} that request.`);
    }
  }

  async function cancel(id: string): Promise<void> {
    const note = window.prompt('Reason for cancelling (optional):') ?? '';
    try {
      await apiFetch(`/api/pto/requests/${id}/cancel`, {
        method: 'POST',
        json: { note: note.trim() || undefined },
      });
      toast.success('Cancelled — the days go back into their balance.');
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not cancel that request.');
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-sm text-[var(--color-body)]">
          PTO approvals are admin-only. Your own balance is on{' '}
          <Link to="/pto" className="text-[var(--color-t3-blue)] hover:underline">
            My PTO
          </Link>
          .
        </p>
      </Card>
    );
  }

  const eligible = roster?.filter((r) => r.balance.eligible) ?? [];
  const notAccruing = roster?.filter((r) => !r.balance.eligible) ?? [];
  const upcoming = (history ?? [])
    .filter((r) => r.endDate >= todayIso())
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Admin</p>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">PTO</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Full-time staff earn <strong>1 day per month</strong> from their start date, reset on
          each person's work anniversary with <strong>no roll-over</strong>, and can take up to{' '}
          <strong>5 days in a row</strong> once earned. The server enforces all three — you can't
          approve leave that hasn't been earned.
        </p>
      </div>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">
          Awaiting decision{pending && pending.length > 0 ? ` (${pending.length})` : ''}
        </h2>
        {pending && pending.length === 0 ? (
          <Card>
            <p className="text-sm text-[var(--color-muted)]">Nothing waiting on you.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {pending?.map((r) => (
              <Card key={r.id} className="!p-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-[var(--color-header)]">{r.agentName}</div>
                    <div className="mt-0.5 text-sm text-[var(--color-body)]">
                      {fmtRange(r)} · {dayLabel(r.days)}
                    </div>
                    {r.reason && (
                      <div className="mt-1 text-sm text-[var(--color-muted)]">“{r.reason}”</div>
                    )}
                    <BalanceHint roster={roster} agentId={r.agentId} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => void decide(r.id, 'approve')}>
                      <Check className="h-4 w-4" />
                      Approve
                    </Button>
                    <Button variant="outline" onClick={() => void decide(r.id, 'deny')}>
                      <X className="h-4 w-4" />
                      Deny
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">Balances</h2>
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] bg-white text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-4 py-2.5">Agent</th>
                  <th className="px-4 py-2.5">Started</th>
                  <th className="px-4 py-2.5">PTO year</th>
                  <th className="px-4 py-2.5 text-right">Earned</th>
                  <th className="px-4 py-2.5 text-right">Taken</th>
                  <th className="px-4 py-2.5 text-right">Pending</th>
                  <th className="px-4 py-2.5 text-right">Available</th>
                  <th className="px-4 py-2.5">Next day</th>
                </tr>
              </thead>
              <tbody>
                {eligible.map((row) => (
                  <tr key={row.agentId} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2.5 font-medium text-[var(--color-header)]">
                      {row.name}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-muted)]">
                      {row.hiredOn ? fmtDate(row.hiredOn, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[var(--color-muted)]">
                      resets {row.balance.yearEnd && fmtDate(row.balance.yearEnd, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2.5 text-right">{fmtDays(row.balance.accruedDays)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtDays(row.balance.usedDays)}</td>
                    <td className="px-4 py-2.5 text-right text-[var(--color-muted)]">
                      {fmtDays(row.balance.pendingDays)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[var(--color-header)]">
                      {fmtDays(row.balance.availableDays)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[var(--color-muted)]">
                      {row.balance.nextAccrualOn
                        ? fmtDate(row.balance.nextAccrualOn, { day: 'numeric', month: 'short' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {notAccruing.length > 0 && (
            <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-xs text-[var(--color-muted)]">
              Not accruing:{' '}
              {notAccruing
                .map(
                  (r) =>
                    `${r.name} (${r.balance.ineligibleReason === 'part_time' ? 'part-time' : 'no start date'})`,
                )
                .join(', ')}
              . Start dates are set on{' '}
              <Link to="/admin/agents" className="text-[var(--color-t3-blue)] hover:underline">
                Agents
              </Link>
              .
            </div>
          )}
        </Card>
      </section>

      <BookOnBehalf roster={eligible} onBooked={load} />

      <AdjustmentForm roster={roster ?? []} onSaved={load} />

      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">Approved &amp; upcoming</h2>
        {upcoming.length === 0 ? (
          <Card>
            <p className="text-sm text-[var(--color-muted)]">Nobody is booked off from today.</p>
          </Card>
        ) : (
          <Card className="!p-0 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {upcoming.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="px-4 py-2.5 font-medium text-[var(--color-header)]">
                      {r.agentName}
                    </td>
                    <td className="px-4 py-2.5">{fmtRange(r)}</td>
                    <td className="px-4 py-2.5 text-[var(--color-muted)]">{dayLabel(r.days)}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${STATUS_CLASSES[r.status]}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => void cancel(r.id)}
                        className="text-xs font-semibold text-[var(--color-danger)] hover:underline"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}

function BalanceHint({
  roster,
  agentId,
}: {
  roster: PtoRosterRow[] | null;
  agentId: string;
}): React.ReactElement | null {
  const row = roster?.find((r) => r.agentId === agentId);
  if (!row?.balance.eligible) return null;
  return (
    <div className="mt-2 text-xs text-[var(--color-muted)]">
      {fmtDays(row.balance.accruedDays)} earned this year · {fmtDays(row.balance.usedDays)} taken ·{' '}
      <strong className="text-[var(--color-header)]">
        {fmtDays(row.balance.availableDays)} available
      </strong>{' '}
      (this request included)
    </div>
  );
}

// Admins can log leave for someone who asked off-system. It books and approves
// in one step — there is nothing left to decide — and lands in the audit log.
function BookOnBehalf({
  roster,
  onBooked,
}: {
  roster: PtoRosterRow[];
  onBooked: () => Promise<void>;
}): React.ReactElement {
  const toast = useToast();
  const [agentId, setAgentId] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [firstDayHalf, setFirstDayHalf] = useState(false);
  const [lastDayHalf, setLastDayHalf] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<PtoPreview | null>(null);
  const [saving, setSaving] = useState(false);

  const singleDay = startDate === endDate;

  useEffect(() => {
    if (endDate < startDate) setEndDate(startDate);
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    if (!agentId || endDate < startDate) {
      setPreview(null);
      return;
    }
    const params = new URLSearchParams({
      agentId,
      startDate,
      endDate,
      firstDayHalf: String(firstDayHalf),
      lastDayHalf: String(singleDay ? firstDayHalf : lastDayHalf),
    });
    apiFetch<PtoPreview>(`/api/pto/preview?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setPreview(r);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, startDate, endDate, firstDayHalf, lastDayHalf, singleDay]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/pto/requests', {
        method: 'POST',
        json: {
          agentId,
          startDate,
          endDate,
          firstDayHalf,
          lastDayHalf: singleDay ? firstDayHalf : lastDayHalf,
          reason: reason.trim() || undefined,
        },
      });
      toast.success('Booked and approved.');
      setReason('');
      await onBooked();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not book that.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">
        <Plus className="mr-1.5 inline h-4 w-4" />
        Book PTO for someone
      </h2>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        For leave agreed off-system. This books it as already approved.
      </p>
      <form onSubmit={(e) => void submit(e)} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Agent"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            required
          >
            <option value="">Choose…</option>
            {roster.map((r) => (
              <option key={r.agentId} value={r.agentId}>
                {r.name} ({fmtDays(r.balance.availableDays)} available)
              </option>
            ))}
          </Select>
          <FormField
            label="First day"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <FormField
            label="Last day"
            type="date"
            min={startDate}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
            <input
              type="checkbox"
              checked={firstDayHalf}
              onChange={(e) => setFirstDayHalf(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-border)]"
            />
            {singleDay ? 'Half day' : 'First day is a half day'}
          </label>
          {!singleDay && (
            <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
              <input
                type="checkbox"
                checked={lastDayHalf}
                onChange={(e) => setLastDayHalf(e.target.checked)}
                className="h-4 w-4 rounded border-[var(--color-border)]"
              />
              Last day is a half day
            </label>
          )}
        </div>
        <FormField
          label="Note (optional)"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
        />
        {preview && (
          <div
            className={[
              'rounded-[var(--radius-button)] border px-3 py-2.5 text-sm',
              preview.problem
                ? 'border-[var(--color-danger)]/40 bg-red-50/60 text-[var(--color-danger)]'
                : 'border-[var(--color-border)] bg-white text-[var(--color-body)]',
            ].join(' ')}
          >
            {preview.problem ? preview.problem.message : `This costs ${dayLabel(preview.days)}.`}
          </div>
        )}
        <Button type="submit" disabled={saving || !agentId || !preview || Boolean(preview.problem)}>
          {saving ? 'Booking…' : 'Book and approve'}
        </Button>
      </form>
    </Card>
  );
}

// The lever for everything the accrual formula can't know: days carried over
// by agreement, a correction after a bad start date, days granted or clawed
// back. Applies to the PTO year containing the effective date, so it expires
// with that year exactly like accrued days do.
function AdjustmentForm({
  roster,
  onSaved,
}: {
  roster: PtoRosterRow[];
  onSaved: () => Promise<void>;
}): React.ReactElement {
  const toast = useToast();
  const [agentId, setAgentId] = useState('');
  const [days, setDays] = useState('1');
  const [effectiveOn, setEffectiveOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const parsedDays = useMemo(() => Number(days), [days]);
  const valid =
    agentId &&
    note.trim().length > 0 &&
    Number.isFinite(parsedDays) &&
    parsedDays !== 0 &&
    Math.abs(parsedDays * 2 - Math.round(parsedDays * 2)) < 1e-9;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/pto/adjustments', {
        method: 'POST',
        json: { agentId, days: parsedDays, effectiveOn, note: note.trim() },
      });
      toast.success('Adjustment saved.');
      setNote('');
      await onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save that adjustment.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">Adjust a balance</h2>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        Grant or remove days outside the normal accrual — carry-over agreed as an exception, a
        correction, a goodwill day. Positive adds, negative removes. Half days allowed. The
        adjustment belongs to the PTO year containing its date and expires with it.
      </p>
      <form onSubmit={(e) => void submit(e)} className="mt-4 grid gap-4 sm:grid-cols-4">
        <Select label="Agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
          <option value="">Choose…</option>
          {roster.map((r) => (
            <option key={r.agentId} value={r.agentId}>
              {r.name}
            </option>
          ))}
        </Select>
        <FormField
          label="Days"
          type="number"
          step="0.5"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          required
        />
        <FormField
          label="Effective"
          type="date"
          value={effectiveOn}
          onChange={(e) => setEffectiveOn(e.target.value)}
          required
        />
        <FormField
          label="Why"
          value={note}
          maxLength={500}
          placeholder="Carry-over agreed with…"
          onChange={(e) => setNote(e.target.value)}
          required
        />
        <div className="sm:col-span-4">
          <Button type="submit" variant="secondary" disabled={saving || !valid}>
            {saving ? 'Saving…' : 'Save adjustment'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
